import { useCallback } from "react";
import type { ShopReviewSection } from "@/types/content";

interface ShopReviewSectionBlockProps {
  section: ShopReviewSection;
}

function rankBadge(rank: number) {
  const base = "w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full text-xs md:text-sm font-bold shrink-0";
  if (rank === 1) return `${base} bg-amber-100 text-amber-700`;
  if (rank === 2) return `${base} bg-slate-200 text-slate-600`;
  if (rank === 3) return `${base} bg-orange-100 text-orange-700`;
  return `${base} bg-slate-100 text-slate-500`;
}

function rankText(rank: number) {
  return rank <= 3 ? `第 ${rank} 名` : `${rank}.`;
}

export default function ShopReviewSectionBlock({
  section,
}: ShopReviewSectionBlockProps) {
  const { title, shops } = section;

  const scrollToShop = useCallback((index: number) => {
    const el = document.getElementById(`shop-review-${index}`);
    if (el) {
      const navHeight = 72;
      const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  return (
    <section className="w-full py-10 md:py-14">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {title && (
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6 md:mb-8">
            {title}
          </h2>
        )}

        {/* 目录导航 */}
        {shops.length > 1 && (
          <nav className="mb-8 md:mb-10">
            <p className="text-sm font-medium text-slate-500 mb-3">快速跳轉</p>
            <div className="flex flex-wrap gap-2">
              {shops.map((shop, i) => (
                <button
                  key={shop._key}
                  onClick={() => scrollToShop(i)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-600 hover:border-primary/40 hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                    i === 0 ? "bg-amber-100 text-amber-700" :
                    i === 1 ? "bg-slate-200 text-slate-600" :
                    i === 2 ? "bg-orange-100 text-orange-700" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {i + 1}
                  </span>
                  <span className="max-w-[140px] truncate">{shop.name}</span>
                </button>
              ))}
            </div>
          </nav>
        )}

        {/* 店家对比总表 */}
        {shops.length > 1 && (
          <div className="mb-10 md:mb-12 bg-white rounded-xl border border-slate-100 overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-table-line text-slate-500" />
              </div>
              <h3 className="text-sm font-semibold text-slate-700">店家總覽</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 w-16">排名</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">店家名稱</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">評分</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">價格</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">地址</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">推薦語</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((shop, i) => (
                    <tr key={shop._key} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                          i === 0 ? "bg-amber-100 text-amber-700" :
                          i === 1 ? "bg-slate-200 text-slate-600" :
                          i === 2 ? "bg-orange-100 text-orange-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => scrollToShop(i)}
                          className="text-sm font-semibold text-slate-700 hover:text-primary transition-colors text-left cursor-pointer"
                        >
                          {shop.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-sm font-semibold text-slate-700">
                          <i className="ri-star-fill text-amber-400 text-xs" />
                          {shop.rating}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{shop.priceRange}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[160px] truncate">{shop.address}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px] truncate">{shop.recommendation || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 店家评测列表 */}
        <div className="space-y-8 md:space-y-10">
          {shops.map((shop, i) => (
            <article
              key={shop._key}
              id={`shop-review-${i}`}
              className="bg-white rounded-xl border border-slate-100 overflow-hidden scroll-mt-20"
            >
              {/* Top: Image + Basic Info */}
              <div className="flex flex-col md:flex-row">
                {shop.image && (
                  <div className="w-full md:w-56 h-48 md:h-auto flex-shrink-0 relative">
                    <img
                      loading="lazy"
                      src={shop.image}
                      alt={shop.name}
                      className="w-full h-full object-cover"
                    />
                    {/* 排名角标 */}
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold backdrop-blur-sm ${
                        i === 0 ? "bg-amber-500/90 text-white" :
                        i === 1 ? "bg-slate-500/80 text-white" :
                        i === 2 ? "bg-orange-500/80 text-white" :
                        "bg-black/60 text-white"
                      }`}>
                        <span className="w-4 h-4 flex items-center justify-center">
                          {i === 0 ? <i className="ri-trophy-line" /> :
                           i === 1 ? <i className="ri-medal-line" /> :
                           i === 2 ? <i className="ri-award-line" /> :
                           <i className="ri-hashtag" />}
                        </span>
                        {rankText(i + 1)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex-1 p-5 md:p-6">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      {!shop.image && (
                        <span className={rankBadge(i + 1)}>
                          {i + 1}
                        </span>
                      )}
                      <h3 className="text-xl font-bold text-slate-800">
                        {shop.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <i className="ri-star-fill text-amber-400 text-sm" />
                      <span className="text-lg font-bold text-slate-800">
                        {shop.rating}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  {shop.tags && shop.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {shop.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 bg-slate-50 rounded-full text-xs font-medium text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-price-tag-3-line" />
                      </div>
                      {shop.priceRange}
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      {shop.address}
                    </span>
                  </div>

                  {/* Recommendation */}
                  {shop.recommendation && (
                    <div className="bg-primary/5 border border-primary/10 rounded-lg px-4 py-3 mb-3">
                      <p className="text-sm text-slate-700 leading-relaxed">
                        <span className="font-semibold text-primary">推薦語：</span>
                        {shop.recommendation}
                      </p>
                    </div>
                  )}

                  {/* Pros & Cons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    {shop.pros && shop.pros.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-check-line" />
                          </div>
                          優點
                        </h4>
                        <ul className="space-y-1">
                          {shop.pros.map((pro, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-slate-600 flex items-start gap-1.5"
                            >
                              <span className="text-emerald-500 mt-0.5">+</span>
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {shop.cons && shop.cons.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-rose-700 mb-2 flex items-center gap-1.5">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-close-line" />
                          </div>
                          缺點
                        </h4>
                        <ul className="space-y-1">
                          {shop.cons.map((con, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-slate-600 flex items-start gap-1.5"
                            >
                              <span className="text-rose-500 mt-0.5">−</span>
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom CTA */}
              {shop.storeUrl && (
                <div className="px-5 md:px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex justify-end">
                  <a
                    href={shop.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
                  >
                    查看店家
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </div>
                  </a>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}